import { useRuleCreateMutation } from "@/common/api/rule/use-rule-create.mutation";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { useUserRuleAttributes } from "@/common/hooks/use-user-rule-attributes";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { Button, ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Loader, LoaderSize } from "@/components/ui/loader";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "wouter";
import { RuleEditorPanels, useRuleUserPreview } from "./_components/rule-affected-users-preview";
import { RuleConfigurationHintDialog } from "./_components/rule-configuration-hint-dialog";
import { RuleFormFields, formSchema, formSchemaType } from "./_components/rule-form-fields";

export function RuleCreateView() {
  const { t } = useTranslation(["ram", "common"]);
  const [_, navigate] = useLocation();
  const params = useParams<{ groupUuid: string }>();
  const { isPending: ruleCreateMutationIsPending, mutateAsync } = useRuleCreateMutation();
  const {
    companies,
    companyGroups,
    branches,
    occupations,
    departments,
    brands,
    jobFunctions,
    isPending: userRuleAttributesIsPending,
  } = useUserRuleAttributes();

  const form = useForm<formSchemaType>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      companies: [],
      companyGroups: [],
      branches: [],
      occupations: [],
      departments: [],
      brands: [],
      jobFunctions: [],
    },
  });

  const { hasAnySelection, userPreviewBody, affectedUsersCount } = useRuleUserPreview(form.control);
  const pageTitle = t("ram:CreateRule");
  const pageSubtitle = t("ram:SelectCriteriaToSeePreviewOfAffectedUsers");

  const onSubmit = async (values: formSchemaType) => {
    await toastMutationStatus({
      mutationFn: () => mutateAsync({ ...values, groupUuid: params.groupUuid }),
      successMessage: t("ram:RuleHasBeenAdded"),
      errorMessage: t("ram:ErrorInCreatingTheRule"),
    });

    navigate(`~/ram/group/${params.groupUuid}`);
  };

  if (userRuleAttributesIsPending) {
    return (
      <CenteredLayout variant={CenteredLayoutVariant.Default} title={pageTitle} subTitle={pageSubtitle}>
        <div className="flex min-h-[40vh] items-center justify-center">
          <Loader size={LoaderSize.Large} />
        </div>
      </CenteredLayout>
    );
  }

  return (
    <CenteredLayout variant={CenteredLayoutVariant.Default} title={pageTitle} subTitle={pageSubtitle}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <RuleEditorPanels
            configurationAction={<RuleConfigurationHintDialog />}
            configurationTitle={t("ram:RuleConfiguration")}
            hasAnySelection={hasAnySelection}
            previewTitle={t("ram:AffectedUsersCount", { count: affectedUsersCount })}
            userPreviewBody={userPreviewBody}
          >
            <RuleFormFields
              isLoading={ruleCreateMutationIsPending}
              formControl={form.control}
              companies={companies?.data}
              companyGroups={companyGroups?.data}
              branches={branches?.data}
              occupations={occupations?.data}
              departments={departments?.data}
              brands={brands?.data}
              jobFunctions={jobFunctions?.data}
            />
          </RuleEditorPanels>

          <div className="flex flex-wrap justify-end gap-4">
            <Button
              disabled={ruleCreateMutationIsPending}
              onClick={() => navigate(`~/ram/group/${params.groupUuid}`)}
              type="button"
              variant={ButtonVariant.Outlined}
              colorVariant={ButtonColorVariant.Secondary}
            >
              {t("common:Cancel")}
            </Button>
            <Button disabled={ruleCreateMutationIsPending || !form.formState.isValid} type="submit">
              {t("common:Save")}
            </Button>
          </div>
        </form>
      </Form>
    </CenteredLayout>
  );
}
